import { useState } from "react";
import { useDispatch } from "react-redux";
import useMerchantPortalAxios, {
  useMerchantPortalFormData,
} from "../../utils/axios";
import {
  setError,
  setFeatureData,
  setTableData,
  setTableLoading,
} from "../store/slices/appSlice";
import { normalizeLastId } from "../utils/loadMore";

function base64ToFile(dataUrl, fileName) {
  if (!dataUrl) return null;

  const [meta, content] = String(dataUrl).split(",");
  if (!meta || !content) return null;

  const mimeType = meta.split(":")[1]?.split(";")[0] || "application/octet-stream";
  const byteCharacters = atob(content);
  const byteArray = new Uint8Array(byteCharacters.length);

  for (let index = 0; index < byteCharacters.length; index += 1) {
    byteArray[index] = byteCharacters.charCodeAt(index);
  }

  return new File([byteArray], fileName || "upload", {
    type: mimeType,
  });
}

export default function useNewS2SAPI() {
  const dispatch = useDispatch();
  const client = useMerchantPortalAxios();
  const formClient = useMerchantPortalFormData();
  const [s2sData, setS2sData] = useState([]);
  const [s2sLoading, setS2sLoading] = useState(true);
  const [totalNbOfPage, setTotalNbOfPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);

  async function GetS2STransfersList(lastId, dateFrom, dateTo) {
    const normalizedLastId = normalizeLastId(lastId);
    setS2sLoading(true);
    dispatch(setTableLoading({ key: "suyoolToSuyool", loading: true }));
    dispatch(setError(null));

    try {
      const res = await client.post("/v1/S2S/GetS2STransfersList", {
        SessionID: sessionStorage.getItem("SessionID"),
        lastId: normalizedLastId,
        DateFrom: dateFrom,
        DateTo: dateTo,
        PageSize: 10,
      });

      const transfers = res?.data?.Payload?.SuyoolToSuyoolTransactions || [];
      setS2sData(transfers);

      if (transfers.length === 0) {
        setTotalNbOfPage(0);
        setTotalCount(0);
      } else {
        setTotalNbOfPage(transfers[0]?.TotalPages || 0);
        setTotalCount(transfers[0]?.TotalCount || 0);
      }

      dispatch(
        setTableData({
          key: "suyoolToSuyool",
          data: transfers,
          pagination: {
            lastId: normalizedLastId,
            totalPages:
              transfers.length > 0 ? transfers[0]?.TotalPages || 0 : 0,
            totalCount:
              transfers.length > 0 ? transfers[0]?.TotalCount || 0 : 0,
            pageSize: 10,
          },
        }),
      );
      dispatch(
        setFeatureData({
          section: "summary",
          key: "suyoolToSuyool",
          value: {
            totalPages: transfers.length > 0 ? transfers[0]?.TotalPages || 0 : 0,
            totalCount: transfers.length > 0 ? transfers[0]?.TotalCount || 0 : 0,
            lastId: normalizedLastId,
          },
        }),
      );

      if (res?.data?.Payload?.TemplateURL) {
        sessionStorage.setItem("S2sTransferTemplate", res.data.Payload.TemplateURL);
      }

      return transfers;
    } catch (error) {
      dispatch(
        setTableData({
          key: "suyoolToSuyool",
          data: [],
          pagination: {
            lastId: normalizedLastId,
            totalPages: 0,
            totalCount: 0,
            pageSize: 10,
          },
        }),
      );
      dispatch(
        setError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "Unable to load Suyool to Suyool transfers.",
        ),
      );
      throw error;
    } finally {
      setS2sLoading(false);
      dispatch(setTableLoading({ key: "suyoolToSuyool", loading: false }));
    }
  }

  async function PushS2STransfer(payload) {
    const nextPayload = {
      ...payload,
      ReasonFile: payload?.ReasonFile
        ? base64ToFile(payload.ReasonFile, payload.ReasonFileName)
        : payload?.ReasonFile,
    };

    const response = await formClient.post("/v1/S2S/PushS2STransfer", nextPayload);
    return response?.data;
  }

  async function PushBulkS2STransfer(payload) {
    const nextPayload = {
      ...payload,
      formFile: payload?.formFile
        ? base64ToFile(payload.formFile, payload.ReasonFileName)
        : payload?.formFile,
    };
    delete nextPayload.ReasonFileName;

    const response = await formClient.post(
      "/v1/S2S/PushBulkS2STransfer",
      nextPayload,
    );
    return response?.data;
  }

  async function GetSuggestedCorporateNames(name) {
    const response = await client.post("/v1/S2S/GetSuggestedCorporateNames", {
      SessionID: sessionStorage.getItem("SessionID"),
      Name: name,
    });

    return response?.data;
  }

  async function GetS2STransferConfig() {
    const response = await client.post("/v1/S2S/GetS2STransferConfig", {
      SessionID: sessionStorage.getItem("SessionID"),
    });

    if (response?.data?.Payload?.TemplateURL) {
      sessionStorage.setItem("S2sTransferTemplate", response.data.Payload.TemplateURL);
    }

    return response?.data;
  }

  async function GetS2STransferSuggestions(InputText, typeId) {
    const response = await client.post("/v1/S2S/GetS2STransferSuggestions", {
      SessionID: sessionStorage.getItem("SessionID"),
      InputText,
      AccountTypeId: typeId,
    });

    return response?.data;
  }

  return {
    GetS2STransfersList,
    PushS2STransfer,
    PushBulkS2STransfer,
    GetSuggestedCorporateNames,
    GetS2STransferConfig,
    GetS2STransferSuggestions,
    s2sData,
    s2sLoading,
    totalNbOfPage,
    totalCount,
  };
}
